import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary';

interface BaseButtonProps {
  readonly variant?: ButtonVariant;
  readonly children: ReactNode;
}

interface ButtonAsButtonProps
  extends BaseButtonProps, Omit<ComponentPropsWithoutRef<'button'>, keyof BaseButtonProps> {
  readonly as?: 'button';
}

interface ButtonAsAnchorProps
  extends BaseButtonProps, Omit<ComponentPropsWithoutRef<'a'>, keyof BaseButtonProps> {
  readonly as: 'a';
}

type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

function isAnchorProps(props: ButtonProps): props is ButtonAsAnchorProps {
  return props.as === 'a';
}

export function Button(props: ButtonProps) {
  const { variant = 'primary' } = props;
  const className = `${styles.button} ${styles[variant]}`;

  if (isAnchorProps(props)) {
    const { as, variant: _v, ...anchorProps } = props;
    void as; // explicitly ignore
    void _v;
    return <a className={className} {...anchorProps} />;
  }

  const { as, variant: _v, ...buttonProps } = props;
  void as;
  void _v;
  return <button className={className} type="button" {...buttonProps} />;
}
